import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, Tv, Sparkles, MousePointerClick, CheckCircle2 } from 'lucide-react';

const pillars = [
  {
    name: 'AI Share of Voice',
    sub: 'How Often Does Your Client Appear?',
    desc: "We run your client's target queries — the questions their ideal customers ask — across ChatGPT, Gemini, and Perplexity. We track how often your client appears in the response vs their top 5 competitors.",
    items: ['Weekly query monitoring', 'Share of voice by engine', 'Competitor gap analysis', 'Trend over time'],
    reported: '% of queries where client appears vs competitors'
  },
  {
    name: 'Branded Search Lift',
    sub: 'Is Brand Awareness Actually Growing?',
    desc: "Branded keyword searches — people searching directly for your client's business name — spike when AI visibility improves and when CTV campaigns are active. We track this via Google Search Console and correlate it to both signals.",
    items: ['Branded search volume tracking', 'Before/after campaign comparison', 'Google Search Console integration', 'Month-on-month trend'],
    reported: '% change in branded searches vs prior period'
  },
  {
    name: 'CTV → AI Correlation',
    sub: 'Does TV Drive AI Search Visibility?',
    desc: 'When MarqiCast and MarqiRank run together, we can measure something no other platform offers: the correlation between a CTV campaign flight and an increase in AI citations. TV makes people search. Now we can prove it.',
    items: ['CTV flight window mapping', 'AI citation frequency during vs outside flights', 'Branded search correlation', 'Full-funnel attribution report'],
    reported: 'AI citation lift % during active CTV flight windows'
  }
];

const funnel = [
  { icon: <Tv className="w-5 h-5" />, title: 'CTV Impression on Hulu / Peacock / Disney+', metric: 'Households reached, completion rate' },
  { icon: <Sparkles className="w-5 h-5" />, title: 'Brand Recall — Viewer remembers the business', metric: 'Branded search lift during CTV flight' },
  { icon: <MousePointerClick className="w-5 h-5" />, title: 'AI Search — Buyer asks ChatGPT for a recommendation', metric: 'AI Share of Voice during CTV flight window' },
  { icon: <CheckCircle2 className="w-5 h-5" />, title: 'Cited & Converts — Client appears. Customer books.', metric: 'Website visits, calls, appointments' }
];

const reportContains = [
  'AI Share of Voice — this month vs last month',
  'Top queries where client appears',
  'Competitor comparison — top 5 by citation frequency',
  'Branded search volume trend',
  'AI engine breakdown — ChatGPT vs Gemini vs Perplexity',
  'Actions taken this month by Marqi team',
  'Recommended focus for next month'
];

const altBg = { background: 'rgba(74, 53, 128, 0.07)' };

const RankMeasurementPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--rank-bg)' }} data-testid="rank-measurement-page">
      <Helmet><title>MarqiRank — AI Visibility Measurement</title></Helmet>

      {/* Hero */}
      <section className="py-24 md:py-32 text-center">
        <div className="container max-w-4xl">
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="rank-measurement-hero-title">
            For the First Time, You Can Measure Your Clients'
            <br /><span className="text-[var(--rank-accent)]">Presence in AI Search.</span>
          </h1>
          <p className="body-lg mb-12 max-w-3xl mx-auto">
            Most agencies can't answer the question: 'How often does my business appear when someone asks
            ChatGPT for a recommendation?' MarqiRank measurement changes that. We track AI Share of Voice,
            citation frequency, competitor comparison, and — uniquely — the correlation between CTV
            campaign flights and AI search visibility.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="rank-measurement-cta-primary">
              See a Sample Report
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link to="/contact" className="btn-secondary">Book a Strategy Session</Link>
          </div>
        </div>
      </section>

      {/* 3 pillars */}
      <section className="py-24" style={altBg}>
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">Three Metrics That Didn't Exist Two Years Ago.</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((p, i) => (
              <div key={p.name} className="feature-card animate-fade-in" style={{ animationDelay: `${i * 0.12}s` }}>
                <h3 className="h3 text-[var(--rank-accent)] mb-2">{p.name}</h3>
                <p className="text-[var(--text-primary)] font-semibold mb-4">{p.sub}</p>
                <p className="body-md mb-6">{p.desc}</p>
                <ul className="space-y-3 mb-6">
                  {p.items.map((it) => (
                    <li key={it} className="flex items-start space-x-3">
                      <Check className="w-4 h-4 text-[var(--rank-accent)] flex-shrink-0 mt-1" />
                      <span className="text-[var(--text-secondary)] text-sm">{it}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 border-t border-[var(--border-subtle)] text-sm"><span className="text-[var(--rank-accent)] font-semibold">Reported as: </span><span className="text-[var(--text-muted)]">{p.reported}</span></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full funnel report */}
      <section className="py-24">
        <div className="container">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">The Report That Closes the Loop.</h2>
            <p className="body-lg">Run MarqiCast and MarqiRank together and you get something no other platform offers: a single report showing the complete modern buyer journey — from streaming TV impression to AI citation to conversion.</p>
          </div>
          <div className="feature-card max-w-3xl mx-auto">
            <h3 className="text-sm uppercase tracking-wider text-[var(--rank-accent)] font-semibold mb-8 text-center">The Full-Funnel Attribution Report</h3>
            <div className="space-y-2">
              {funnel.map((f, i) => (
                <React.Fragment key={f.title}>
                  <div className="flex items-center space-x-4 p-4 bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-subtle)] animate-fade-in" style={{ animationDelay: `${i * 0.15}s` }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-[var(--rank-accent)] flex-shrink-0" style={{ background: 'rgba(74,53,128,0.18)' }}>{f.icon}</div>
                    <div>
                      <div className="text-[var(--text-primary)] font-semibold text-sm">{f.title}</div>
                      <div className="text-[var(--text-muted)] text-xs mt-1"><span className="text-[var(--rank-accent)]">Metric: </span>{f.metric}</div>
                    </div>
                  </div>
                  {i < funnel.length - 1 && (
                    <div className="flex justify-center"><div className="w-px h-5 bg-[var(--rank-primary)]"></div></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          <p className="text-center text-[var(--text-muted)] mt-10 max-w-2xl mx-auto text-sm">Full-funnel attribution report available to agencies running both MarqiCast and MarqiRank. Individual measurement reports available for either service independently.</p>
        </div>
      </section>

      {/* White-label reporting */}
      <section className="py-24" style={altBg}>
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">Every Report Is Under Your Brand.</h2>
          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-5xl mx-auto">
            <div className="feature-card">
              <h3 className="h3 text-[var(--text-primary)] mb-6">What the Report Contains</h3>
              <ul className="space-y-3 mb-6">
                {reportContains.map((r) => (
                  <li key={r} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[var(--rank-accent)] flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-secondary)] text-sm">{r}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-[var(--text-muted)] pt-4 border-t border-[var(--border-subtle)]"><span className="text-[var(--rank-accent)] font-semibold">Optional add-on: </span>CTV → AI correlation report for MarqiCast clients</p>
            </div>
            <div>
              <h3 className="h3 text-[var(--text-primary)] mb-6">How Agencies Use It</h3>
              <p className="body-md mb-6">The monthly report is designed to be sent directly to your client — in PDF or dashboard format — under your agency name and logo. No Marqi branding appears anywhere. Most agency partners present the report in their monthly client review call. The AI Share of Voice chart — showing the client outranking competitors in ChatGPT answers — is consistently the moment clients say 'we need to do more of this.'</p>
              <blockquote className="border-l-2 border-[var(--rank-primary)] pl-6 italic text-[var(--text-secondary)]">
                "The report pays for itself in client retention."
                <span className="block not-italic text-sm text-[var(--text-muted)] mt-2">— Agency partner, UK</span>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container">
          <div className="feature-card text-center max-w-4xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">See What Your Client's AI Visibility Looks Like Right Now.</h2>
            <p className="body-lg mb-12 max-w-2xl mx-auto">We'll produce a sample measurement report for one of your existing clients — showing their current AI Share of Voice, top competitor citations, and the queries where they're missing. Free, no obligation.</p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect">Get a Free Measurement Report<ArrowRight className="ml-2 w-5 h-5" /></Link>
              <Link to="/rank" className="btn-secondary">Back to MarqiRank Overview</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RankMeasurementPage;
